import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useOrgStore } from '~~/stores/org'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { useDiscardUnsavedGuard } from '~/composables/ui/useDiscardUnsavedGuard'
import { useOrgReorderSortable } from '~/composables/orga/reorder/useOrgReorderSortable'
import { cloneReorderGroups, countMovedKeys, isReorderPermutation, movedItemsLabel, reorderGroupsEqual } from '~/composables/orga/reorder/reorderModel'
import type { OrgGridReorderOptions } from '~/composables/orga/reorder/orgGridReorderTypes'
import {
  applyDomOrder,
  discardReorder,
  moveByKeyboard,
  onEditModeChange,
  onEscapeKey,
  requestCancel,
  saveReorder,
  cancelDraft,
  startReorder,
  type ReorderSession,
} from '~/composables/orga/reorder/orgGridReorderSession'

function refreshReorderBaseline(session: ReorderSession): void {
  const current = session.options.readGroups()
  if (!session.options.enabled.value) {
    session.initialGroups.value = cloneReorderGroups(current)
    return
  }
  if (isReorderPermutation(session.initialGroups.value, current)) {
    return
  }
  session.initialGroups.value = cloneReorderGroups(current)
  void startReorder(session)
}

function wireSession(session: ReorderSession): void {
  watch(session.options.isEditing, function watchEditMode(editing) {
    onEditModeChange(session, editing)
  })
  watch(session.options.enabled, function watchPermission(allowed) {
    if (allowed) {
      void startReorder(session)
      return
    }
    if (!session.isDirty.value) {
      session.isReordering.value = false
      session.sortable.destroy()
    }
  })
  watch(
    function groupSignature() {
      return session.options.readGroups()
        .map(function describeGroup(group) {
          return `${group.id}:${group.keys.join('|')}`
        })
        .join(';')
    },
    function refreshBaseline() {
      refreshReorderBaseline(session)
    },
  )
  onBeforeRouteLeave(function guardLeave(to) {
    if (!session.isReordering.value || !session.isDirty.value || session.allowLeave.value) {
      return true
    }
    session.pendingPath.value = to.fullPath
    session.requestClose()
    return false
  })
  onMounted(function listenEscape() {
    window.addEventListener('keydown', onEscape)
    if (session.options.enabled.value) {
      void startReorder(session)
    }
  })
  onBeforeUnmount(function cleanupReorder() {
    window.removeEventListener('keydown', onEscape)
    session.sortable.destroy()
  })

  function onEscape(event: KeyboardEvent): void {
    onEscapeKey(session, event)
  }
}

function createSession(options: OrgGridReorderOptions): ReorderSession {
  const orgStore = useOrgStore()
  const isReordering = ref(false)
  const saving = ref(false)
  const announcement = ref('')
  const initialGroups = ref(options.readGroups())
  const pendingPath = ref<string | null>(null)
  const closeEditAfterDiscard = ref(false)
  const allowLeave = ref(false)
  const isDirty = computed(() => !reorderGroupsEqual(options.readGroups(), initialGroups.value))
  const session: ReorderSession = {
    options,
    toast: useSourceToast(),
    isReordering,
    saving,
    announcement,
    initialGroups,
    pendingPath,
    closeEditAfterDiscard,
    allowLeave,
    sortable: { rootRef: ref(null), bind: async function bindLater() {}, destroy: function destroyLater() {} },
    isDirty,
    setEditing: function setEditing(value: boolean) {
      orgStore.setIsEditing(value)
    },
    requestClose: function requestClose() {
      return undefined
    },
  }
  session.sortable = useOrgReorderSortable(function onDomOrder(groupId, keys) {
    applyDomOrder(session, groupId, keys)
  })
  return session
}

export function useOrgGridReorder(options: OrgGridReorderOptions) {
  const session = createSession(options)
  const discard = useDiscardUnsavedGuard({
    isDirty: session.isDirty,
    loading: session.saving,
    onDiscard: function onDiscard() {
      discardReorder(session)
    },
  })
  session.requestClose = discard.requestClose
  wireSession(session)
  const movedLabel = computed(() =>
    movedItemsLabel(countMovedKeys(options.readGroups(), session.initialGroups.value)),
  )

  return {
    rootRef: session.sortable.rootRef,
    visible: options.enabled,
    isReordering: session.isReordering,
    saving: session.saving,
    announcement: session.announcement,
    isDirty: session.isDirty,
    movedLabel,
    showDiscardConfirm: discard.showDiscardConfirm,
    requestCancel: () => requestCancel(session),
    cancelDraft: () => cancelDraft(session),
    save: () => saveReorder(session),
    moveByKeyboard: (groupId: string, keyName: string, fromIndex: number) =>
      moveByKeyboard(session, groupId, keyName, fromIndex),
    confirmDiscard: discard.confirmDiscard,
    cancelDiscard: function cancelDiscardDialog() {
      session.pendingPath.value = null
      session.closeEditAfterDiscard.value = false
      discard.cancelDiscard()
    },
  }
}
